import React, { useCallback, useState } from 'react';
import type { FC } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import SvgIcon from '@mui/material/SvgIcon';

import PageTitle from '../../../ui/PageTitle/PageTitle';
import BackButton from '../../../ui/BackButton/BackButton';
import FormField from '../../../ui/FormField/FormField';
import { CANCEL_BUTTON } from '../../../shared/strings';
import noop from '../../../utils/function/noop';
import Button from '../../../ui/Button/Button';
import useFormConfigManager from '../../../hooks/useFormConfigManager/useFormConfigManager';
import {
  TEXT_CONTENT_IDS,
  TITLE_IDS,
} from '../../../utils/element-ids';
import { SETUP_PAYMENT_METHOD_BUTTON_TEXT } from '../../add-payment-method/dev';
import fieldHasCardNumber from '../../../utils/function/fieldHasCardNumber';
import {
  NICKNAME_ON_CARD_CANNOT_INCLUDE_CARD_NUMBER_ERROR,
  PAYMENT_METHOD_SETTINGS_TITLE,
} from '../../pay-and-store/constants';
import { isInvalidNickname } from '../../../utils/function/nicknameUtils';
import Checkbox from '../../../ui/Checkbox/Checkbox';
import DropdownV2 from '../../../ui/Dropdown/DropdownV2';
import { getCardSvg } from '../../checkout/components/Wallet/utils/getCardSvg';
import Divider from '../../../ui/Divider/Divider';
import Disclaimer from '../../../components/Disclaimer/Disclaimer';
import { useLanguageDropdownController } from '../hooks/useLanguageDropdownController';
import IconRenderer from '../../../ui/Icons/IconRenderer';
import ManufacturerAddCardFieldContainer from '../../add-payment-method/components/ManufacturerCard/ManufacturerAddCardFieldContainer';
import type { MedicationOption } from '../../../shared/types/CardCategories/MedicationOption';
import type { CardCategoriesConfig } from '../../../shared/types/CardCategories/CardCategoriesConfig';

type SycurioPhoneEntryBaseProps = {
  nickname?: string;
  cardHolderName: string;
  manufacturerCard: boolean;
  isDefault: boolean;
  cardType: string;
  lastFour: string;
  expYear: string;
  expMonth: string;
  avsPostalCode: string;
  medication: string;
};

export type SycurioSubmitFormData =
  SycurioPhoneEntryBaseProps & {
    token: string;
    hasCapturedSycurioPhoneEntry: boolean;
    country?: string;
  };
type SycurioPaymentMethodFormKeys = keyof SycurioSubmitFormData;
type SycurioPaymentMethodFormValues =
  SycurioSubmitFormData[keyof SycurioSubmitFormData];

type SycurioSubmitFormErrors = Partial<
  Record<keyof SycurioSubmitFormData, string>
>;

export type SycurioPhoneEntrySubmitProps = {
  formTitle: string;
  medicationOptions: MedicationOption[];
  cardCategoriesConfigObject: CardCategoriesConfig;
  sycurioData: Omit<
    SycurioSubmitFormData,
    | 'token'
    | 'hasCapturedSycurioPhoneEntry'
    | 'manufacturerCard'
    | 'isDefault'
    | 'nickname'
    | 'medication'
  >;
  onBackClick?: () => void;
  onCancel: () => void;
  onSubmit: (data: SycurioPaymentMethodFormSubmitData) => void;
};
const validate = ({
  nickname,
  manufacturerCard,
  medication,
}: SycurioPhoneEntryBaseProps): SycurioSubmitFormErrors => {
  const formErrors: SycurioSubmitFormErrors = {};

  if (isInvalidNickname(nickname)) {
    formErrors.nickname = 'Please enter a valid card nickname.';
  }
  if (nickname && fieldHasCardNumber(nickname)) {
    formErrors.nickname =
      NICKNAME_ON_CARD_CANNOT_INCLUDE_CARD_NUMBER_ERROR;
  }
  if (manufacturerCard && medication === '') {
    formErrors.medication =
      'A medication selection is required.';
  }

  return formErrors;
};

export type SycurioPaymentMethodFormSubmitData = {
  nameOnCard: string;
  nickname?: string;
  manufacturerCard: boolean;
  isDefault: boolean;
  medication: string;
};

const SycurioPhoneEntrySubmitView: FC<
  SycurioPhoneEntrySubmitProps
> = ({
  formTitle,
  sycurioData,
  onBackClick,
  onCancel = noop,
  onSubmit,
  medicationOptions,
  cardCategoriesConfigObject,
}) => {
  const [formErrors, setFormErrors] =
    useState<SycurioSubmitFormErrors>({});

  const [formData, setFormData] =
    useState<SycurioPhoneEntryBaseProps>({
      cardHolderName: sycurioData.cardHolderName,
      nickname: '',
      manufacturerCard: false,
      isDefault: false,
      cardType: sycurioData.cardType,
      lastFour: `••••    ••••    ••••    ${sycurioData.lastFour}`,
      expYear: sycurioData.expYear,
      expMonth: sycurioData.expMonth,
      avsPostalCode: sycurioData.avsPostalCode,
      medication: '',
    });

  const paymentMethodIconType = getCardSvg(
    sycurioData?.cardType ?? 'genericcard',
    sycurioData?.cardType,
  );

  const setFormFields = (
    fields: Partial<SycurioSubmitFormData>,
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      ...fields,
    }));
  };
  const handleOnSubmit = useCallback(() => {
    const validations = validate(formData);
    setFormErrors(validations);
    if (Object.keys(validations).length) {
      return;
    }

    onSubmit({
      ...sycurioData,
      nickname: formData.nickname,
      nameOnCard: formData.cardHolderName,
      manufacturerCard: formData.manufacturerCard,
      isDefault: formData.isDefault,
      medication: formData.medication,
    });
  }, [
    formData.medication,
    formData.nickname,
    formData.cardHolderName,
    formData.manufacturerCard,
    formData.isDefault,
    sycurioData.cardType,
    sycurioData.country,
    sycurioData.expMonth,
    sycurioData.expYear,
    sycurioData.lastFour,
    sycurioData.avsPostalCode,
  ]);
  const handleOnChange = (
    key: SycurioPaymentMethodFormKeys,
    value: SycurioPaymentMethodFormValues,
  ) => {
    setFormFields({ [key]: value });

    const isUpdatedToManufacturerCard =
      key === 'manufacturerCard' && value;

    if (isUpdatedToManufacturerCard) {
      setFormFields({ isDefault: false });
    }
    const isDefaultKey = key === 'isDefault';
    if (isDefaultKey && value) {
      setFormFields({
        manufacturerCard: false,
        medication: '',
      });
    }
  };
  const handleOnBlur = (
    key: SycurioPaymentMethodFormKeys,
    value: SycurioPaymentMethodFormValues,
  ) => {
    const newFormValues = { ...formData, [key]: value };

    const validationResult = validate(newFormValues);

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [key]: validationResult[key],
    }));

    setFormFields(newFormValues);
  };

  const { showActionsOnForm, title, titleTestId } =
    useFormConfigManager({
      title: formTitle,
      titleTestId: TITLE_IDS.MAIN_PAGE_TITLE,
      backActionConfig: {
        label: 'Back',
        testId: TEXT_CONTENT_IDS.ADD_PAYMENT_METHOD_BACK_BUTTON,
        handler: onBackClick,
      },
      primaryActionConfig: {
        label: SETUP_PAYMENT_METHOD_BUTTON_TEXT,
        testId: TEXT_CONTENT_IDS.ADD_PAYMENT_METHOD_SAVE_BUTTON,
        handler: handleOnSubmit,
      },
      secondaryActionConfig: {
        label: CANCEL_BUTTON,
        testId:
          TEXT_CONTENT_IDS.ADD_PAYMENT_METHOD_CANCEL_BUTTON,
        handler: onCancel,
      },
      showBackButton: !!onBackClick,
    });

  useLanguageDropdownController();

  return (
    <Box>
      {showActionsOnForm && onBackClick ? (
        <BackButton
          label="Back"
          onClick={onBackClick}
        />
      ) : null}
      <PageTitle
        title={title}
        titleId={titleTestId}
      />
      <Box sx={{ my: '24px' }}>
        <Typography
          style={{
            fontSize: '20.25px',
            fontWeight: 700,
            lineHeight: '24px',
          }}
          data-testid={
            TEXT_CONTENT_IDS.PAYMENT_TELEPHONIC_SUBMIT_VIEW_SUB_TITLE
          }
        >
          Card details
        </Typography>
      </Box>
      <Box sx={{ my: '24px' }}>
        <Typography variant="body2">
          All fields are required unless marked optional.
        </Typography>
      </Box>
      <FormField
        id="nameOnCard"
        label="Name on card"
        placeholder=""
        value={formData.cardHolderName}
        errorMessage={formErrors.cardHolderName}
        required
        autocomplete="name"
        onChange={noop}
        disabled
      />
      <FormField
        id="cardNumber"
        label="Card number"
        value={formData.lastFour}
        disabled
        onChange={noop}
        endAdornment={
          <SvgIcon
            data-testid="sycurioPaymentMethodIcon"
            component={paymentMethodIconType}
            width="47px"
            height="26px"
            viewBox="0 0 42 26"
            preserveAspectRatio="xMidYMid meet"
          />
        }
      />

      <Grid
        container
        className="gridCell"
        spacing={2}
      >
        <Grid size={{ xs: 6 }}>
          <FormField
            id="expiration"
            label="Expiration date"
            inputMode="numeric"
            placeholder="MM / YY"
            disabled
            value={`${formData.expMonth} / ${formData.expYear}`}
            required
            onChange={noop}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <FormField
            id="cvc"
            type="number"
            inputMode="numeric"
            label="Security code"
            placeholder="***"
            disabled
            value="***"
            required
            onChange={noop}
            endAdornment={
              <IconRenderer
                name="cvvIcon"
                testId="cvvIcon"
              />
            }
          />
        </Grid>
      </Grid>
      {sycurioData.avsPostalCode !== '' && (
        <Grid
          container
          direction="column"
          sx={{
            gap: '12px',
          }}
        >
          <DropdownV2
            value="United States"
            disabled
            id="country"
            label="Country"
          />
          <FormField
            id="zipCode"
            label="ZIP code"
            placeholder="Jane Smith"
            value={formData.avsPostalCode}
            required
            disabled
            autocomplete="name"
            onChange={noop}
          />
        </Grid>
      )}
      <Grid
        container
        direction="column"
        sx={{
          marginTop: '16px',
        }}
      >
        <FormControl component="fieldset">
          <FormLabel component="legend">
            <Typography
              style={{ fontWeight: 700 }}
              data-testid={
                TEXT_CONTENT_IDS.PAYMENT_METHOD_SETTINGS_LABEL
              }
            >
              {PAYMENT_METHOD_SETTINGS_TITLE}
            </Typography>
          </FormLabel>

          <ManufacturerAddCardFieldContainer
            cardCategoriesConfigObject={
              cardCategoriesConfigObject
            }
            manufacturerCardCheckboxProps={{
              id: 'isManufacturerCard',
              value: formData.manufacturerCard,
              onChange: (value: boolean): void => {
                handleOnChange('manufacturerCard', value);
              },
            }}
            medicationSelectorDropdownProps={{
              id: 'medicationDropdown',
              value: formData.medication,
              options: medicationOptions,
              errorMessage: formErrors.medication,
              onChange: (value: string) => {
                handleOnChange('medication', value);
              },
              onBlur: (value: string) => {
                handleOnBlur('medication', value);
              },
            }}
          />
          {!formData.manufacturerCard ? (
            <Checkbox
              id="isDefault"
              value={formData.isDefault}
              label="Set as default payment method"
              testId={
                TEXT_CONTENT_IDS.ADD_PAYMENT_SET_AS_DEFAULT_CHECKBOX
              }
              onChange={(value) => {
                handleOnChange('isDefault', value);
              }}
            />
          ) : null}
        </FormControl>
      </Grid>
      <Grid
        container
        direction="column"
        sx={{
          marginTop: '4px',
        }}
      >
        <FormField
          id="cardNickname"
          label="Card nickname"
          placeholder=""
          value={formData.nickname}
          errorMessage={formErrors.nickname}
          required
          helperText="Card nickname must be 30 characters or less and can contain letters and numbers only."
          hintText="(Optional)"
          autocomplete="name"
          onChange={(value) => {
            handleOnChange('nickname', value);
          }}
          onBlur={(value) => {
            handleOnBlur('nickname', value);
          }}
        />
      </Grid>
      <Grid style={{ paddingTop: '12px' }}>
        <Divider />
      </Grid>
      <Grid style={{ margin: '24px 0 0' }}>
        <Disclaimer actionType="add_payment_method" />
      </Grid>
      {showActionsOnForm ? (
        <Grid
          container
          direction="column"
          sx={{ gap: '16px', marginTop: '24px' }}
          position="sticky"
          display="absolute"
        >
          <Button
            onClick={handleOnSubmit}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
          <Button
            isLoading={false}
            onClick={onCancel}
            variant="contained"
            color="secondary"
          >
            {CANCEL_BUTTON}
          </Button>
        </Grid>
      ) : null}
    </Box>
  );
};

export default SycurioPhoneEntrySubmitView;
